import React, { useEffect, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { registerServiceWorker } from "./utils/pushNotifications";
import RedCodeAlert from "./components/RedCodeAlert";
import OfflineIndicator from "./components/OfflineIndicator";
import ErrorBoundary from "./components/ErrorBoundary";
import lazy from "./lib/lazyWithRetry";
import useAppBadge from "./hooks/useAppBadge";
import useAutoEnablePush from "./hooks/useAutoEnablePush";
import ScrollToTop from "./components/ScrollToTop";

// Layout - loaded immediately
import Layout from "./components/layout/Layout";

// Auth pages - loaded immediately (first interaction)
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ForceChangePassword from "./pages/ForceChangePassword";
import ManagerReports from "./pages/ManagerReports";
import Concerns from "./pages/Concerns";

// Critical pages - loaded immediately to avoid preview env chunk loading failures
import CustomizableDashboard from "./pages/CustomizableDashboard";
import DashboardV2 from "./pages/DashboardV2";
import OperationsCommandCenter from "./pages/OperationsCommandCenter";
import CEODashboard from "./pages/CEODashboard";
import Profile from "./pages/Profile";
import OperationsControlCenter from "./pages/admin/OperationsControlCenter";
import FranchiseDashboard from "./pages/FranchiseDashboard";
import FranchiseStores from "./pages/FranchiseStores";
const FranchiseNewLead = lazy(() => import("./pages/FranchiseNewLead"));
const FranchiseLeadDetail = lazy(() => import("./pages/FranchiseLeadDetail"));
import FranchiseFinancialDashboard from "./pages/FranchiseFinancialDashboard";
import WhatsAppChat from "./pages/WhatsAppChat";
import Connect from "./pages/Connect";
import Notifications from "./pages/Notifications";
import NotificationsPage from "./pages/NotificationsPage";

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="text-center">
      <div className="spinner mx-auto mb-4" />
      <p className="text-slate-600">Loading...</p>
    </div>
  </div>
);

// Lazy loaded pages - only loaded when navigated to
const TaskList = lazy(() => import("./pages/TaskList"));
const TaskDetails = lazy(() => import("./pages/TaskDetails"));
const TaskForm = lazy(() => import("./pages/TaskForm"));
const DailyUpdate = lazy(() => import("./pages/DailyUpdate"));
const GroupList = lazy(() => import("./pages/GroupList"));
const GroupForm = lazy(() => import("./pages/GroupForm"));
const GroupDetails = lazy(() => import("./pages/GroupDetails"));
const GroupChat = lazy(() => import("./pages/GroupChat"));
const DailyUpdatesAdmin = lazy(() => import("./pages/DailyUpdatesAdmin"));
// Notifications - now loaded immediately (see top imports)
const Settings = lazy(() => import("./pages/Settings"));
const Reminders = lazy(() => import("./pages/Reminders"));
const CalendarView = lazy(() => import("./pages/CalendarView"));
const Chat = lazy(() => import("./pages/Chat"));
// Customer Demand System
const CustomerDemandForm = lazy(() => import("./pages/CustomerDemandForm"));
const MyDemands = lazy(() => import("./pages/MyDemands"));
const DemandsHub = lazy(() => import("./pages/DemandsHub"));
const IncidentsHub = lazy(() => import("./pages/IncidentsHub"));
const FeedbackHub = lazy(() => import("./pages/FeedbackHub"));
const DemandManagement = lazy(() => import("./pages/DemandManagement"));
// WhatsAppChat - now loaded immediately (see top imports)
// Profile - now loaded immediately (see top imports)
const StaffPeople = lazy(() => import("./pages/StaffPeople"));
const Resources = lazy(() => import("./pages/Resources"));
const AdminResources = lazy(() => import("./pages/AdminResources"));
const SOPView = lazy(() => import("./pages/SOPView"));
const ChecklistComplete = lazy(() => import("./pages/ChecklistComplete"));
const FormSubmit = lazy(() => import("./pages/FormSubmit"));
const AdminRoster = lazy(() => import("./pages/AdminRoster"));
const MyShifts = lazy(() => import("./pages/MyShifts"));
const ShiftApproval = lazy(() => import("./pages/ShiftApproval"));
const RosterApproval = lazy(() => import("./pages/RosterApproval"));
const TimeClock = lazy(() => import("./pages/admin/TimeClock"));
const SwapRequests = lazy(() => import("./pages/admin/SwapRequests"));
const Payroll = lazy(() => import("./pages/admin/Payroll"));
const Analytics = lazy(() => import("./pages/admin/Analytics"));
const Records = lazy(() => import("./pages/admin/Records"));
const ComplianceDashboard = lazy(() => import("./pages/admin/ComplianceDashboard"));
const StoreComplianceDetail = lazy(() => import("./pages/admin/StoreComplianceDetail"));
const ProjectManagerDashboard = lazy(() => import("./pages/admin/ProjectManagerDashboard"));
const FormBuilder = lazy(() => import("./pages/admin/FormBuilder"));
const TemperatureLogs = lazy(() => import("./pages/admin/TemperatureLogs"));
const TemperatureLogEntry = lazy(() => import("./pages/TemperatureLogEntry"));
const StoreScorecard = lazy(() => import("./pages/admin/StoreScorecard"));
const CorrectiveActions = lazy(() => import("./pages/admin/CorrectiveActions"));
const ComplianceTracker = lazy(() => import("./pages/admin/ComplianceTracker"));
const LeaveManagement = lazy(() => import("./pages/admin/LeaveManagement"));
const DocumentSigning = lazy(() => import("./pages/admin/DocumentSigning"));
const IncidentTracking = lazy(() => import("./pages/admin/IncidentTracking"));
const ProfileCompletion = lazy(() => import("./pages/admin/ProfileCompletion"));
const PerformanceReviews = lazy(() => import("./pages/admin/PerformanceReviews"));
const PerformanceReviewForm = lazy(() => import("./pages/admin/PerformanceReviewForm"));
const PerformanceReviewDetail = lazy(() => import("./pages/admin/PerformanceReviewDetail"));
const EmployeeReviewHistory = lazy(() => import("./pages/admin/EmployeeReviewHistory"));
const StaffKPI = lazy(() => import("./pages/admin/StaffKPI"));
const KPIReviewForm = lazy(() => import("./pages/admin/KPIReviewForm"));
const MonthlyReviews = lazy(() => import("./pages/admin/MonthlyReviews"));
const MonthlyReviewForm = lazy(() => import("./pages/admin/MonthlyReviewForm"));
const StoreLeadershipDashboard = lazy(() => import("./pages/admin/StoreLeadershipDashboard"));
const StoreLeadershipReviewForm = lazy(() => import("./pages/admin/StoreLeadershipReviewForm"));
const MyPerformance = lazy(() => import("./pages/MyPerformance"));
const TeamScoring = lazy(() => import("./pages/TeamScoring"));
const MyTraining = lazy(() => import("./pages/MyTraining"));
const HRTrainingManagement = lazy(() => import("./pages/HRTrainingManagement"));
const TrainingHub = lazy(() => import("./pages/TrainingHub"));
const TrainingHubAdmin = lazy(() => import("./pages/TrainingHubAdmin"));
const TrainingComplianceDashboard = lazy(() => import("./pages/TrainingComplianceDashboard"));
const TRMApprovals = lazy(() => import("./pages/TRMApprovals"));
const CertificateManagement = lazy(() => import("./pages/CertificateManagement"));
const MyStorePerformance = lazy(() => import("./pages/MyStorePerformance"));
const Training = lazy(() => import("./pages/Training"));
const Kiosk = lazy(() => import("./pages/Kiosk"));
const KioskLauncher = lazy(() => import("./pages/KioskLauncher"));
const QuickClock = lazy(() => import("./pages/QuickClock"));
// CustomizableDashboard - now loaded immediately (see top imports)
const StoreChecklist = lazy(() => import("./pages/StoreChecklist"));
// Connect - now loaded immediately (see top imports)
const HRDashboard = lazy(() => import("./pages/hr/HRDashboard"));
const OnboardingDetail = lazy(() => import("./pages/hr/OnboardingDetail"));
const DeboardingDetail = lazy(() => import("./pages/hr/DeboardingDetail"));
const IDVerificationDetail = lazy(() => import("./pages/hr/IDVerificationDetail"));
const NewOnboarding = lazy(() => import("./pages/hr/NewOnboarding"));
const NewDeboarding = lazy(() => import("./pages/hr/NewDeboarding"));
const IDUpload = lazy(() => import("./pages/IDUpload"));

// Warehouse Pages
const WarehouseDashboard = lazy(() => import("./pages/WarehouseDashboard"));
const DeliveryReportForm = lazy(() => import("./pages/warehouse/DeliveryReportForm"));
const DeliveryReportsList = lazy(() => import("./pages/warehouse/DeliveryReportsList"));
const BillingConfirmations = lazy(() => import("./pages/warehouse/BillingConfirmations"));

// Driver Pages
const DriverDashboard = lazy(() => import("./pages/driver/DriverDashboard"));
const DriverTracking = lazy(() => import("./pages/admin/DriverTracking"));
const DriverKPIReview = lazy(() => import("./pages/admin/DriverKPIReview"));
const DriverKPIReports = lazy(() => import("./pages/admin/DriverKPIReports"));
const DriverReports = lazy(() => import("./pages/admin/DriverReports"));

// Warehouse/Dispatch Pages
const DispatchCalendar = lazy(() => import("./pages/warehouse/DispatchCalendar"));
const StoreDeliveries = lazy(() => import("./pages/store/StoreDeliveries"));

const Recognition = lazy(() => import("./pages/admin/Recognition"));
const Gamification = lazy(() => import("./pages/Gamification"));
const SkillsMatrix = lazy(() => import("./pages/admin/SkillsMatrix"));
const CertificationsDashboard = lazy(() => import("./pages/admin/CertificationsDashboard"));
const TrainingProgressDashboard = lazy(() => import("./pages/TrainingProgressDashboard"));
const VoiceSettings = lazy(() => import("./pages/admin/VoiceSettings"));
const JobTraining = lazy(() => import("./pages/admin/skills/JobTraining"));
const TrainingModuleDetail = lazy(() => import("./pages/admin/skills/TrainingModuleDetail"));
const TrainingAdminManagement = lazy(() => import("./pages/admin/skills/TrainingAdminManagement"));

// Unavailability
const MyUnavailability = lazy(() => import("./pages/MyUnavailability"));
const UnavailabilityApproval = lazy(() => import("./pages/admin/UnavailabilityApproval"));

// Policies
const Policies = lazy(() => import("./pages/Policies"));
const IssueReporting = lazy(() => import("./pages/IssueReporting"));
const StoreGrade = lazy(() => import("./pages/StoreGrade"));

// Operations Control Center - now loaded immediately (see top imports)

// Department Dashboards
// DepartmentDashboard removed Feb 2026 — feature retired.

// Team Dashboards
const TeamDashboards = lazy(() => import("./pages/admin/TeamDashboards"));
const UserDashboardView = lazy(() => import("./pages/admin/UserDashboardView"));

// User Management
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));

// Onboarding
const OnboardingWizard = lazy(() => import("./pages/admin/OnboardingWizard"));
const BulkOnboarding = lazy(() => import("./pages/admin/BulkOnboarding"));
const ExecutiveSummary = lazy(() => import("./pages/admin/ExecutiveSummary"));

// Expiry Tracking
const QuickExpiryReport = lazy(() => import("./pages/QuickExpiryReport"));
const ProductExpiryList = lazy(() => import("./pages/ProductExpiryList"));
const StoreWiseExpiryDashboard = lazy(() => import("./pages/StoreWiseExpiryDashboard"));
const ComplianceExpiryTracker = lazy(() => import("./pages/ComplianceExpiryTracker"));
const HRDocumentExpiry = lazy(() => import("./pages/HRDocumentExpiry"));

// Opening Checklist
const OpeningChecklist = lazy(() => import("./pages/OpeningChecklist"));
const OpeningChecklistReview = lazy(() => import("./pages/OpeningChecklistReview"));

// Reporting
const Reporting = lazy(() => import("./pages/Reporting"));
const QuickReport = lazy(() => import("./pages/QuickReport"));
const IncidentReport = lazy(() => import("./pages/IncidentReport"));

// Projects
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));

// Store Bidding
const StoreBidding = lazy(() => import("./pages/StoreBidding"));
const TechPanel = lazy(() => import("./pages/TechPanel"));
const TechControlPanel = lazy(() => import("./pages/admin/TechControlPanel"));

// Points & Redemption
const PointsRedemption = lazy(() => import("./pages/PointsRedemption"));

// Site Control & Renewals (Office Admin only)
const SiteControlRenewals = lazy(() => import("./pages/SiteControlRenewals"));

// Supplier Reconciliation
const SupplierReconciliation = lazy(() => import("./pages/SupplierReconciliation"));

// Task Management System
const TaskManagement = lazy(() => import("./pages/TaskManagement"));
const TaskManagementForm = lazy(() => import("./pages/TaskManagementForm"));
const TaskManagementDetail = lazy(() => import("./pages/TaskManagementDetail"));

// Unified Calendar
const UnifiedCalendar = lazy(() => import("./pages/UnifiedCalendar"));

// Bookkeeper
const OwnershipStructure = lazy(() => import("./pages/OwnershipStructure"));

// Inspections
const Inspections = lazy(() => import("./pages/Inspections"));
const InspectionForm = lazy(() => import("./pages/InspectionForm"));
const InspectionDetail = lazy(() => import("./pages/InspectionDetail"));
const InspectionKPIDashboard = lazy(() => import("./pages/InspectionKPIDashboard"));
const InspectionChecklistEditor = lazy(() => import("./pages/InspectionChecklistEditor"));

// Driver Logbook
const DriverLogbook = lazy(() => import("./pages/driver/DriverLogbook"));
const VehicleCheck = lazy(() => import("./pages/driver/VehicleCheck"));

// Site Supervisor Dashboard
const SiteSupervisorDashboard = lazy(() => import("./pages/SiteSupervisorDashboard"));

// Marketing Pages
const MarketingCampaigns = lazy(() => import("./pages/marketing/Campaigns"));
const SocialMedia = lazy(() => import("./pages/marketing/SocialMedia"));
const MarketingContent = lazy(() => import("./pages/marketing/Content"));
const MarketingAnalytics = lazy(() => import("./pages/marketing/Analytics"));

// Smart Meeting Booking
const SmartMeetings = lazy(() => import("./pages/SmartMeetings"));

// Franchise Invoices (6-stage approval workflow)
const FranchiseInvoices = lazy(() => import("./pages/FranchiseInvoices"));

// Equipment Service Tracking
const EquipmentServiceHub = lazy(() => import("./pages/EquipmentServiceHub"));
const EquipmentDetail = lazy(() => import("./pages/EquipmentDetail"));
const EquipmentForm = lazy(() => import("./pages/EquipmentForm"));
const ServiceRecordForm = lazy(() => import("./pages/ServiceRecordForm"));

// Expense Dashboard
const ExpenseDashboard = lazy(() => import("./pages/ExpenseDashboard"));

// Team Structure
const TeamStructure = lazy(() => import("./pages/TeamStructure"));

// Org Chart
const OrgChart = lazy(() => import("./pages/OrgChart"));
const OrgChartV2 = lazy(() => import("./pages/OrgChartV2"));
const OrganizationSummary = lazy(() => import("./pages/OrganizationSummary"));

// Finance - BAS & Tax Returns
const BASLodgments = lazy(() => import("./pages/BASLodgments"));
const TaxReturnsAudits = lazy(() => import("./pages/TaxReturnsAudits"));
const TaxLodgementsDashboard = lazy(() => import("./pages/TaxLodgementsDashboard"));

// MD Dashboard
const MDDashboard = lazy(() => import("./pages/MDDashboard"));

// Notifications Page - now loaded immediately (see top imports)

// Franchise Management - now loaded immediately (see top imports)
// Leads features removed entirely (Apr 2026 — sidebar rebuild).
const FranchiseOnboarding = lazy(() => import("./pages/FranchiseOnboarding"));
const OnboardingTFNForm = lazy(() => import("./pages/OnboardingTFNForm"));
const MyOnboardingForm = lazy(() => import("./pages/MyOnboardingForm"));
// FranchiseStores - now loaded immediately (see top imports)
const FranchiseIssues = lazy(() => import("./pages/FranchiseIssues"));
// FranchiseFinancialDashboard - now loaded immediately (see top imports)
const StoreFinancialEntry = lazy(() => import("./pages/StoreFinancialEntry"));
const StoreBudgetSettings = lazy(() => import("./pages/StoreBudgetSettings"));
// Leads pipeline report removed
const StoreOpeningTimeline = lazy(() => import("./pages/StoreOpeningTimeline"));
const StoreComplianceReport = lazy(() => import("./pages/StoreComplianceReport"));

// Staff Reports
const ReportAndImprove = lazy(() => import("./pages/ReportAndImprove"));
const MyActivity = lazy(() => import("./pages/MyActivity"));

// Drill-down Reports (Quick Access)
const StaffOverviewReport = lazy(() => import("./pages/admin/StaffOverviewReport"));
const StoreHealthReport = lazy(() => import("./pages/admin/StoreHealthReport"));
const AnalyticsHub = lazy(() => import("./pages/admin/AnalyticsHub"));

// Venue Management & Time Attendance
const VenueManagement = lazy(() => import("./pages/admin/VenueManagement"));
const VenueKiosk = lazy(() => import("./pages/admin/VenueKiosk"));
const ShiftApprovals = lazy(() => import("./pages/admin/ShiftApprovals"));
const PayrollDashboard = lazy(() => import("./pages/admin/PayrollDashboard"));
const HRDashboardPage = lazy(() => import("./pages/admin/HRDashboardPage"));

// System Health
const SystemHealth = lazy(() => import("./pages/SystemHealth"));

// Import Pipeline
const ImportPipeline = lazy(() => import("./pages/ImportPipeline"));

// Office Admin System
const OfficeAdminDashboard = lazy(() => import("./pages/OfficeAdminDashboard"));
const OfficeAdminExpenses = lazy(() => import("./pages/OfficeAdminExpenses"));
const OfficeAdminExpenseForm = lazy(() => import("./pages/OfficeAdminExpenseForm"));
const OfficeAdminExpenseDetail = lazy(() => import("./pages/OfficeAdminExpenseDetail"));
const OfficeAdminTasks = lazy(() => import("./pages/OfficeAdminTasks"));
const OfficeAdminTaskForm = lazy(() => import("./pages/OfficeAdminTaskForm"));
const OfficeAdminTaskDetail = lazy(() => import("./pages/OfficeAdminTaskDetail"));

// Inventory Management System
const InventoryDashboard = lazy(() => import("./pages/InventoryDashboard"));
const InventoryProducts = lazy(() => import("./pages/InventoryProducts"));
const InventoryProductForm = lazy(() => import("./pages/InventoryProductForm"));
const InventoryCategories = lazy(() => import("./pages/InventoryCategories"));
const InventorySuppliers = lazy(() => import("./pages/InventorySuppliers"));
const InventoryImport = lazy(() => import("./pages/InventoryImport"));

// Purchasing System
const PurchaseOrders = lazy(() => import("./pages/PurchaseOrders"));
const PurchaseOrderForm = lazy(() => import("./pages/PurchaseOrderForm"));
const PurchaseOrderDetail = lazy(() => import("./pages/PurchaseOrderDetail"));
const PurchasePipeline = lazy(() => import("./pages/PurchasePipeline"));
const PurchaseWorkflow = lazy(() => import("./pages/PurchaseWorkflow"));

// Sales System
const Customers = lazy(() => import("./pages/Customers"));
const CustomerForm = lazy(() => import("./pages/CustomerForm"));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));
const SalesOrders = lazy(() => import("./pages/SalesOrders"));
const SalesOrderForm = lazy(() => import("./pages/SalesOrderForm"));
const SalesOrderDetail = lazy(() => import("./pages/SalesOrderDetail"));
const SalesInvoice = lazy(() => import("./pages/SalesInvoice"));
const SalesPipeline = lazy(() => import("./pages/SalesPipeline"));

// Controls System (Phase 4)
const StockAdjustments = lazy(() => import("./pages/StockAdjustments"));
const StockTakes = lazy(() => import("./pages/StockTakes"));
const StockTakeDetail = lazy(() => import("./pages/StockTakeDetail"));
const CreditNotes = lazy(() => import("./pages/CreditNotes"));

// Mobile Scanner (Phase 6)
const MobileScanner = lazy(() => import("./pages/MobileScanner"));

// Accounts Management
const AccountsDashboard = lazy(() => import("./pages/AccountsDashboard"));
const ObligationsTracker = lazy(() => import("./pages/ObligationsTracker"));

// Receiving List
const ReceivingList = lazy(() => import("./pages/ReceivingList"));
const ReceivingDetail = lazy(() => import("./pages/ReceivingDetail"));
const MobileReceiving = lazy(() => import("./pages/MobileReceiving"));
const PurchaseReview = lazy(() => import("./pages/PurchaseReview"));

// Store Ordering (for Store Managers)
const StoreOrdering = lazy(() => import("./pages/StoreOrdering"));
const StoreOrders = lazy(() => import("./pages/StoreOrders"));
const StoreOrderDetail = lazy(() => import("./pages/StoreOrderDetail"));
const StoreProducts = lazy(() => import("./pages/StoreProducts"));

// Incident Reporting System
const IncidentReportForm = lazy(() => import("./pages/IncidentReportForm"));
const MyIncidentsPage = lazy(() => import("./pages/MyIncidentsPage"));
const IncidentManagement = lazy(() => import("./pages/IncidentManagement"));
const IncidentAnalytics = lazy(() => import("./pages/IncidentAnalytics"));

// Feedback System
const FeedbackForm = lazy(() => import("./pages/FeedbackForm"));
const MyFeedback = lazy(() => import("./pages/MyFeedback"));
const FeedbackManagement = lazy(() => import("./pages/FeedbackManagement"));
const FeedbackInsights = lazy(() => import("./pages/FeedbackInsights"));

// Store Checklists
const ChecklistTemplates = lazy(() => import("./pages/ChecklistTemplates"));
const StoreChecklists = lazy(() => import("./pages/StoreChecklists"));
const ChecklistCompliance = lazy(() => import("./pages/ChecklistCompliance"));
const StoreChecklistHub = lazy(() => import("./pages/StoreChecklistHub"));

// Staff Performance (All Staff view for management)
const StaffPerformance = lazy(() => import("./pages/StaffPerformance"));
// File Management page removed (Apr 2026 — sidebar rebuild). Object storage
// uploads used by inspections/expenses/profile remain intact.
const ContentPlanner = lazy(() => import("./pages/ContentPlanner"));
const ApprovalsInbox = lazy(() => import("./pages/ApprovalsInbox"));
const StockTransfers = lazy(() => import("./pages/StockTransfers"));
const StockTransferRequestDetail = lazy(() => import("./pages/StockTransferRequestDetail"));
const StockTransferPODetail = lazy(() => import("./pages/StockTransferPODetail"));

// Coming Soon placeholder for routes under development
const ComingSoon = lazy(() => import("./pages/ComingSoon"));

// Protected Route Component
function ProtectedRoute({ children, requireAdmin = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Force-change-password gate: when an admin "Reset Password" sets the
  // flag, the user is held on /change-password until they pick a new one.
  if (user.password_must_change && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Force-onboarding gate: TFN/ABN employees must complete their onboarding
  // form before they can access any other page.
  if (user.onboarding_pending && location.pathname !== '/my-onboarding') {
    return <Navigate to="/my-onboarding" replace />;
  }

  if (requireAdmin && user.role === 'staff') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Auth Redirect Component
function AuthRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    // Unified landing: every user lands on the Dashboard regardless of role.
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/login" replace />;
}


// Admin Dashboard Redirect - Shows OCC for top management
function AdminDashboardRedirect() {
  const { user, loading } = useAuth();
  const TOP_MANAGEMENT = ['ceo', 'admin', 'md', 'gm'];
  
  // Wait for auth to load
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[#DC2626]" />
      </div>
    );
  }
  
  if (TOP_MANAGEMENT.includes(user?.role)) {
    return <CEODashboard />;
  }
  return <DashboardV2 />;
}


function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/onboarding/tfn/:token" element={<OnboardingTFNForm />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/kiosk" element={<KioskLauncher />} />
      <Route path="/kiosk/legacy" element={<Kiosk />} />
      <Route path="/kiosk/venue/:venueId" element={<VenueKiosk />} />

      {/* Force-change-password — outside the Layout so the user can't escape
          via the sidebar until they pick a new password. */}
      <Route path="/change-password" element={
        <ProtectedRoute>
          <ForceChangePassword />
        </ProtectedRoute>
      } />

      {/* Mandatory onboarding form — outside the layout for the same reason */}
      <Route path="/my-onboarding" element={
        <ProtectedRoute>
          <MyOnboardingForm />
        </ProtectedRoute>
      } />
      
      {/* Root Redirect */}
      <Route path="/" element={<AuthRedirect />} />

      {/* Legacy alias — redirect outside the protected layout so it is not
          intercepted by the layout's route-guard. */}
      <Route path="/admin/access-control" element={<Navigate to="/admin/tech-control-panel" replace />} />
      {/* Legacy: Venues moved into User Management */}
      <Route path="/admin/venues" element={<Navigate to="/admin/user-management?tab=locations" replace />} />

      {/* Staff Routes */}
      <Route
        element={
          <ProtectedRoute>
            <Layout title="Dashboard" />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardV2 />} />
        <Route path="/dashboard/classic" element={<CustomizableDashboard />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/staff" element={<StaffPeople />} />
        <Route path="/tasks" element={<TaskList />} />
        <Route path="/tasks/new" element={<TaskForm />} />
        <Route path="/tasks/:taskId" element={<TaskDetails />} />
        <Route path="/tasks/:taskId/edit" element={<TaskForm />} />
        <Route path="/groups" element={<GroupList />} />
        <Route path="/groups/new" element={<GroupForm />} />
        <Route path="/groups/:groupId" element={<GroupDetails />} />
        <Route path="/groups/:groupId/edit" element={<GroupForm />} />
        <Route path="/groups/:groupId/chat" element={<GroupChat />} />
        <Route path="/daily-update" element={<DailyUpdate />} />
        <Route path="/reminders" element={<Reminders />} />
        <Route path="/calendar" element={<CalendarView />} />
        <Route path="/expenses" element={<ExpenseDashboard />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/chat/:conversationId" element={<Chat />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/resources/sop/:sopId" element={<SOPView />} />
        <Route path="/resources/checklist/:checklistId" element={<ChecklistComplete />} />
        <Route path="/resources/form/:formId" element={<FormSubmit />} />
        <Route path="/book-meeting" element={<SmartMeetings />} />
        <Route path="/franchise-invoices" element={<FranchiseInvoices />} />
        <Route path="/roster" element={<MyShifts />} />
        <Route path="/roster/my-shifts" element={<MyShifts />} />
        <Route path="/attendance" element={<TimeClock />} />
        <Route path="/leave" element={<LeaveManagement />} />
        <Route path="/time-off" element={<LeaveManagement />} />
        <Route path="/suggestions" element={<ReportAndImprove />} />
        <Route path="/my-unavailability" element={<MyUnavailability />} />
        {/* Customer Demand System */}
        <Route path="/customer-demand" element={<CustomerDemandForm />} />
        <Route path="/my-demands" element={<DemandsHub />} />
        <Route path="/my-demands/list" element={<MyDemands />} />
        <Route path="/purchase/demands" element={<DemandManagement />} />
        <Route path="/purchase/demands/:demandId" element={<DemandManagement />} />
        
        {/* Incident Reporting System */}
        <Route path="/incidents/report" element={<IncidentReportForm />} />
        <Route path="/my-incidents" element={<IncidentsHub />} />
        <Route path="/my-incidents/list" element={<MyIncidentsPage />} />
        <Route path="/incidents" element={<IncidentManagement />} />
        <Route path="/incidents/:incidentId" element={<IncidentManagement />} />
        <Route path="/incidents/analytics" element={<IncidentAnalytics />} />
        
        {/* Feedback System */}
        <Route path="/feedback/submit" element={<FeedbackForm />} />
        <Route path="/my-feedback" element={<FeedbackHub />} />
        <Route path="/my-feedback/list" element={<MyFeedback />} />
        <Route path="/feedback" element={<FeedbackManagement />} />
        <Route path="/feedback/:feedbackId" element={<FeedbackManagement />} />
        <Route path="/feedback/insights" element={<FeedbackInsights />} />
        
        {/* Store Checklists — consolidated hub (canonical route) */}
        <Route path="/store-checklists" element={<StoreChecklistHub />} />
        {/* Legacy paths redirect into the hub. Components kept on disk so
            any deep-link still resolves cleanly. */}
        <Route path="/checklists" element={<Navigate to="/store-checklists" replace />} />
        <Route path="/checklists/templates" element={<Navigate to="/store-checklists" replace />} />
        <Route path="/checklists/compliance" element={<Navigate to="/store-checklists" replace />} />
        
        <Route path="/training" element={<Training />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/quick-clock" element={<QuickClock />} />
        <Route path="/my-dashboard" element={<DashboardV2 />} />
        <Route path="/my-performance" element={<MyPerformance />} />
        <Route path="/staff-performance" element={<StaffPerformance />} />
        {/* /file-management removed Apr 2026 */}
        <Route path="/content-planner" element={<ContentPlanner />} />
        <Route path="/approvals" element={<ApprovalsInbox />} />
        <Route path="/stock-transfers" element={<StockTransfers />} />
        <Route path="/stock-transfers/requests/:id" element={<StockTransferRequestDetail />} />
        <Route path="/stock-transfers/pos/:id" element={<StockTransferPODetail />} />
        <Route path="/team-scoring" element={<TeamScoring />} />
        <Route path="/my-training" element={<MyTraining />} />
        <Route path="/training-hub" element={<TrainingHub />} />
        <Route path="/admin/training-hub" element={<TrainingHubAdmin />} />
        <Route path="/admin/training-compliance" element={<TrainingComplianceDashboard />} />
        <Route path="/hr-training" element={<HRTrainingManagement />} />
        <Route path="/trm-approvals" element={<TRMApprovals />} />
        <Route path="/training-progress" element={<TrainingProgressDashboard />} />
        <Route path="/certificates" element={<CertificateManagement />} />
        <Route path="/my-store-performance" element={<MyStorePerformance />} />
        <Route path="/id-upload" element={<IDUpload />} />
        <Route path="/connect" element={<Connect />} />
        <Route path="/policies" element={<Policies />} />
        <Route path="/issue-reporting" element={<IssueReporting />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/my-store-grade" element={<StoreGrade />} />
        <Route path="/reporting" element={<Reporting />} />
        <Route path="/quick-report" element={<QuickReport />} />
        <Route path="/incident-report" element={<IncidentReport />} />
        
        {/* Expiry Tracking Routes */}
        <Route path="/expiry-report" element={<QuickExpiryReport />} />
        <Route path="/expiry-list" element={<ProductExpiryList />} />
        <Route path="/my-expiry-reports" element={<ProductExpiryList myReportsOnly={true} />} />
        <Route path="/compliance-expiry" element={<ComplianceExpiryTracker />} />
        <Route path="/hr-document-expiry" element={<HRDocumentExpiry />} />
        
        {/* Opening Checklist — legacy, redirect to consolidated hub */}
        <Route path="/opening-checklist" element={<Navigate to="/store-checklists" replace />} />
        <Route path="/opening-checklist/review" element={<Navigate to="/store-checklists" replace />} />
        
        {/* Projects Routes */}
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:projectId" element={<ProjectDetail />} />
        
        {/* Store Bidding */}
        <Route path="/store-bidding" element={<StoreBidding />} />
        <Route path="/admin/tech-panel" element={<TechPanel />} />
        <Route path="/admin/tech-control-panel" element={<TechControlPanel />} />
        
        {/* Points & Redemption */}
        <Route path="/points-redemption" element={<PointsRedemption />} />
        
        {/* Site Control & Renewals (Office Admin) */}
        <Route path="/site-control" element={<SiteControlRenewals />} />
        
        {/* Supplier Reconciliation */}
        <Route path="/supplier-reconciliation" element={<SupplierReconciliation />} />
        
        {/* Task Management System */}
        <Route path="/task-management" element={<TaskManagement />} />
        <Route path="/task-management/new" element={<TaskManagementForm />} />
        <Route path="/task-management/:taskId" element={<TaskManagementDetail />} />
        <Route path="/task-management/:taskId/edit" element={<TaskManagementForm />} />
        
        {/* Unified Calendar */}
        <Route path="/unified-calendar" element={<UnifiedCalendar />} />
        
        {/* Bookkeeper Routes */}
        <Route path="/ownership-structure" element={<OwnershipStructure />} />
        
        {/* Finance Routes - BAS & Tax Returns */}
        <Route path="/bas-lodgments" element={<BASLodgments />} />
        <Route path="/tax-returns" element={<TaxReturnsAudits />} />
        
        {/* Inspection Routes */}
        <Route path="/inspections" element={<Inspections />} />
        <Route path="/inspections/new" element={<InspectionForm />} />
        <Route path="/inspections/checklist-editor" element={<InspectionChecklistEditor />} />
        <Route path="/inspections/kpi" element={<InspectionKPIDashboard />} />
        <Route path="/inspections/kpi-dashboard" element={<InspectionKPIDashboard />} />
        <Route path="/inspections/:id" element={<InspectionDetail />} />
        
        {/* Driver Routes */}
        <Route path="/driver" element={<DriverDashboard />} />
        <Route path="/driver/dashboard" element={<DriverDashboard />} />
        <Route path="/deliveries" element={<DriverDashboard />} />
        <Route path="/driver-logbook" element={<DriverLogbook />} />
        <Route path="/vehicle-check" element={<VehicleCheck />} />
        <Route path="/routes" element={<ComingSoon />} />
        <Route path="/navigation" element={<ComingSoon />} />
      </Route>

      {/* Project Manager Routes */}
      <Route
        element={
          <ProtectedRoute requireAdmin>
            <Layout title="Project Dashboard" />
          </ProtectedRoute>
        }
      >
        <Route path="/pm/dashboard" element={<ProjectManagerDashboard />} />
      </Route>

      {/* Admin Routes */}
      <Route
        element={
          <ProtectedRoute requireAdmin>
            <Layout title="Admin Dashboard" />
          </ProtectedRoute>
        }
      >
        <Route path="/admin" element={<AdminDashboardRedirect />} />
        <Route path="/admin/md-dashboard" element={<MDDashboard />} />
        <Route path="/admin/control-center" element={<OperationsControlCenter />} />
        {/* /admin/dept/:department removed Feb 2026 (Department feature retired) */}
        <Route path="/admin/team-dashboards" element={<TeamDashboards />} />
        <Route path="/admin/user-dashboard/:userId" element={<UserDashboardView />} />
        <Route path="/admin/user-management" element={<UserManagement />} />
        <Route path="/admin/onboarding-wizard" element={<OnboardingWizard />} />
        <Route path="/admin/onboarding-wizard/:userId" element={<OnboardingWizard />} />
        <Route path="/admin/bulk-onboarding" element={<BulkOnboarding />} />
        <Route path="/admin/executive-summary" element={<ExecutiveSummary />} />
        <Route path="/admin/store-expiry-dashboard" element={<StoreWiseExpiryDashboard />} />
        <Route path="/admin/my-dashboard" element={<DashboardV2 />} />
        <Route path="/admin/profile" element={<Profile />} />
        <Route path="/admin/tasks" element={<TaskList />} />
        <Route path="/admin/tasks/new" element={<TaskForm />} />
        <Route path="/admin/tasks/:taskId" element={<TaskDetails />} />
        <Route path="/admin/tasks/:taskId/edit" element={<TaskForm />} />
        <Route path="/admin/groups" element={<GroupList />} />
        <Route path="/admin/groups/new" element={<GroupForm />} />
        <Route path="/admin/groups/:groupId" element={<GroupList />} />
        <Route path="/admin/groups/:groupId/edit" element={<GroupForm />} />
        {/* /admin/staff → User Management (Staff Directory was consolidated
            into User Management; keep these routes as redirects so old
            bookmarks, widget links, and the OnboardingWizard's completion
            redirect still land in the right place.) */}
        <Route path="/admin/staff" element={<Navigate to="/admin/user-management" replace />} />
        <Route path="/admin/staff/new" element={<Navigate to="/admin/user-management" replace />} />
        <Route path="/admin/staff/:userId/edit" element={<Navigate to="/admin/user-management" replace />} />
        <Route path="/admin/updates" element={<DailyUpdatesAdmin />} />
        <Route path="/admin/resources" element={<AdminResources />} />
        <Route path="/admin/roster" element={<AdminRoster />} />
        <Route path="/admin/roster-approval" element={<RosterApproval />} />
        <Route path="/admin/shift-approval" element={<ShiftApproval />} />
        <Route path="/admin/swap-requests" element={<SwapRequests />} />
        <Route path="/admin/unavailability-approval" element={<UnavailabilityApproval />} />
        <Route path="/admin/time-clock" element={<TimeClock />} />
        <Route path="/admin/payroll" element={<Payroll />} />
        <Route path="/admin/analytics" element={<AnalyticsHub />} />
        <Route path="/admin/records" element={<Records />} />
        <Route path="/admin/compliance" element={<ComplianceDashboard />} />
        <Route path="/compliance" element={<ComplianceDashboard />} />
        <Route path="/compliance/store/:venueId" element={<StoreComplianceDetail />} />
        <Route path="/admin/forms" element={<FormBuilder />} />
        <Route path="/admin/temperature-logs" element={<TemperatureLogs />} />
        <Route path="/temp-logs/new" element={<TemperatureLogEntry />} />
        <Route path="/admin/scorecard" element={<StoreScorecard />} />
        <Route path="/admin/corrective-actions" element={<CorrectiveActions />} />
        <Route path="/admin/compliance-tracker" element={<ComplianceTracker />} />
        <Route path="/admin/incidents" element={<IncidentTracking />} />
        <Route path="/admin/leave" element={<LeaveManagement />} />
        <Route path="/admin/documents" element={<DocumentSigning />} />
        <Route path="/admin/training" element={<Training />} />
        <Route path="/admin/reminders" element={<Reminders />} />
        <Route path="/admin/calendar" element={<CalendarView />} />
        
        {/* Equipment Service Tracking */}
        <Route path="/equipment" element={<EquipmentServiceHub />} />
        <Route path="/equipment/new" element={<EquipmentForm />} />
        <Route path="/equipment/:id" element={<EquipmentDetail />} />
        <Route path="/equipment/:id/edit" element={<EquipmentForm />} />
        <Route path="/equipment/:equipmentId/service/new" element={<ServiceRecordForm />} />
        
        <Route path="/admin/expenses" element={<ExpenseDashboard />} />
        <Route path="/admin/team-structure" element={<TeamStructure />} />
        <Route path="/admin/org-chart" element={<OrgChart />} />
        <Route path="/admin/org-chart-v2" element={<OrgChartV2 />} />
        <Route path="/organization" element={<OrganizationSummary />} />
        <Route path="/admin/chat" element={<Chat />} />
        <Route path="/admin/connect" element={<Connect />} />
        <Route path="/admin/my-dashboard" element={<DashboardV2 />} />
        <Route path="/admin/hr" element={<HRDashboard />} />
        <Route path="/admin/hr/onboarding/:userId" element={<OnboardingDetail />} />
        <Route path="/admin/hr/deboarding/:userId" element={<DeboardingDetail />} />
        <Route path="/admin/hr/id-verification/:userId" element={<IDVerificationDetail />} />
        <Route path="/admin/hr/new-onboarding" element={<NewOnboarding />} />
        <Route path="/admin/hr/new-deboarding" element={<NewDeboarding />} />
        <Route path="/admin/profile-completion" element={<ProfileCompletion />} />
        <Route path="/admin/performance-reviews" element={<PerformanceReviews />} />
        <Route path="/admin/performance-reviews/new" element={<PerformanceReviewForm />} />
        <Route path="/admin/performance-reviews/:reviewId" element={<PerformanceReviewDetail />} />
        <Route path="/admin/performance-reviews/:reviewId/edit" element={<PerformanceReviewForm />} />
        <Route path="/admin/performance-reviews/employee/:employeeId" element={<EmployeeReviewHistory />} />
        <Route path="/admin/kpi" element={<StaffKPI />} />
        <Route path="/admin/kpi/new-review" element={<KPIReviewForm />} />
        <Route path="/admin/kpi/review/:reviewId" element={<KPIReviewForm />} />
        <Route path="/admin/monthly-reviews" element={<MonthlyReviews />} />
        <Route path="/admin/monthly-reviews/review/:assignmentId" element={<MonthlyReviewForm />} />
        <Route path="/admin/store-leadership" element={<StoreLeadershipDashboard />} />
        <Route path="/admin/store-leadership/review/:storeName" element={<StoreLeadershipReviewForm />} />
        <Route path="/admin/driver-tracking" element={<DriverTracking />} />
        <Route path="/admin/driver-kpi" element={<DriverKPIReview />} />
        <Route path="/admin/driver-kpi-reports" element={<DriverKPIReports />} />
        <Route path="/admin/driver-reports" element={<DriverReports />} />
        <Route path="/admin/recognition" element={<Recognition />} />
        <Route path="/admin/gamification" element={<Gamification />} />
        <Route path="/gamification" element={<Gamification />} />
        <Route path="/admin/skills-matrix" element={<SkillsMatrix />} />
        <Route path="/admin/certifications" element={<CertificationsDashboard />} />
        <Route path="/admin/training-progress" element={<TrainingProgressDashboard />} />
        <Route path="/admin/voice-settings" element={<VoiceSettings />} />
        <Route path="/admin/job-training" element={<JobTraining />} />
        <Route path="/admin/job-training/:moduleId" element={<TrainingModuleDetail />} />
        <Route path="/admin/training-management" element={<TrainingAdminManagement />} />
        <Route path="/admin/projects" element={<Projects />} />
        <Route path="/admin/projects/:projectId" element={<ProjectDetail />} />
        <Route path="/admin/notifications" element={<Notifications />} />
        <Route path="/admin/settings" element={<Settings />} />
        <Route path="/admin/store-checklist" element={<Navigate to="/store-checklists" replace />} />
        <Route path="/admin/store-checklists" element={<Navigate to="/store-checklists" replace />} />
        <Route path="/admin/policies" element={<Policies />} />

        {/* Drill-down Report Routes (Quick Access) */}
        <Route path="/admin/reports/staff-overview" element={<StaffOverviewReport />} />
        <Route path="/admin/reports/store-health" element={<StoreHealthReport />} />
        
        {/* Franchise Management Routes */}
        <Route path="/franchise" element={<FranchiseDashboard />} />
        <Route path="/franchise/dashboard" element={<FranchiseDashboard />} />
        {/* Leads routes removed Apr 2026 — New Lead form restored Feb 2026 since backend still active */}
        <Route path="/franchise/leads/new" element={<FranchiseNewLead />} />
        <Route path="/franchise/leads/:leadId" element={<FranchiseLeadDetail />} />
        <Route path="/franchise/onboarding" element={<FranchiseOnboarding />} />
        <Route path="/franchise/stores" element={<FranchiseStores />} />
        <Route path="/franchise/issues" element={<FranchiseIssues />} />
        <Route path="/franchise/issues/new" element={<FranchiseIssues />} />
        <Route path="/franchise/financials" element={<FranchiseFinancialDashboard />} />
        <Route path="/franchise/budgets" element={<StoreBudgetSettings />} />
        <Route path="/store-financials/entry" element={<StoreFinancialEntry />} />
        {/* /franchise/leads-report removed Apr 2026 */}
        <Route path="/franchise/opening-timeline" element={<StoreOpeningTimeline />} />
        <Route path="/franchise/opening-timeline/:storeId" element={<StoreOpeningTimeline />} />
        <Route path="/franchise/compliance" element={<StoreComplianceReport />} />
        <Route path="/franchise/compliance/:storeId" element={<StoreComplianceReport />} />
        
        {/* Staff Reports Routes */}
        <Route path="/report" element={<ReportAndImprove />} />
        <Route path="/my-activity" element={<MyActivity />} />
        <Route path="/my-activity/:reportId" element={<MyActivity />} />
        <Route path="/manager/reports" element={<ManagerReports />} />
        <Route path="/admin/concerns" element={<Concerns />} />
        
        {/* Venue Management & Time Attendance */}
        <Route path="/admin/venues" element={<VenueManagement />} />
        <Route path="/admin/shift-approvals" element={<ShiftApprovals />} />
        <Route path="/admin/payroll-dashboard" element={<PayrollDashboard />} />
        <Route path="/admin/hr-dashboard" element={<HRDashboardPage />} />
        <Route path="/admin/system-health" element={<SystemHealth />} />
        <Route path="/admin/attendance" element={<TimeClock />} />
        <Route path="/admin/bas" element={<BASLodgments />} />
        <Route path="/admin/tax-returns" element={<TaxReturnsAudits />} />
        <Route path="/admin/tax-lodgements" element={<TaxLodgementsDashboard />} />
        <Route path="/admin/financial-docs" element={<Records />} />
        <Route path="/admin/franchise-issues" element={<FranchiseIssues />} />
        <Route path="/admin/announcements" element={<GroupList />} />
        <Route path="/projects/new" element={<ProjectDetail />} />
        
        {/* Site Supervisor Dashboard */}
        <Route path="/supervisor" element={<SiteSupervisorDashboard />} />
        <Route path="/supervisor/dashboard" element={<SiteSupervisorDashboard />} />
      </Route>

      {/* Warehouse Routes */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/warehouse" element={<WarehouseDashboard />} />
        <Route path="/warehouse/delivery-report/new" element={<DeliveryReportForm />} />
        <Route path="/warehouse/delivery-reports" element={<DeliveryReportsList />} />
        <Route path="/warehouse/billing-confirmations" element={<BillingConfirmations />} />
        <Route path="/warehouse/inventory" element={<ComingSoon />} />
        <Route path="/warehouse/receiving" element={<ReceivingList />} />
        <Route path="/warehouse/receiving/:receivingId" element={<ReceivingDetail />} />
        <Route path="/warehouse/receive/:poId" element={<MobileReceiving />} />
        {/* Alias: /mobile-receiving → warehouse receiving list (pending POs).
            Without this, warehouse users clicking a stale link land on /admin. */}
        <Route path="/mobile-receiving" element={<Navigate to="/warehouse/receiving" replace />} />
        <Route path="/purchase/review/:poId" element={<PurchaseReview />} />
        <Route path="/warehouse/dispatch" element={<DispatchCalendar />} />
        <Route path="/store/deliveries" element={<StoreDeliveries />} />
        <Route path="/warehouse/reports" element={<ComingSoon />} />
      </Route>

      {/* Store Manager Ordering Routes */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/store/order" element={<StoreOrdering />} />
        <Route path="/store/orders" element={<StoreOrders />} />
        <Route path="/store/orders/:orderId" element={<StoreOrderDetail />} />
        <Route path="/store/products" element={<StoreProducts />} />
      </Route>

      {/* Purchase Manager Routes */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/purchase-orders" element={<ComingSoon />} />
        <Route path="/vendors" element={<ComingSoon />} />
        <Route path="/import-pipeline" element={<ImportPipeline />} />
      </Route>

      {/* Import Manager Routes */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/imports" element={<ComingSoon />} />
        <Route path="/shipments" element={<ComingSoon />} />
        <Route path="/customs" element={<ComingSoon />} />
      </Route>

      {/* Marketing Routes */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/marketing/campaigns" element={<MarketingCampaigns />} />
        <Route path="/marketing/social" element={<SocialMedia />} />
        <Route path="/marketing/content" element={<MarketingContent />} />
        <Route path="/marketing/analytics" element={<MarketingAnalytics />} />
      </Route>

      {/* Office Admin System Routes */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/office-admin" element={<OfficeAdminDashboard />} />
        <Route path="/office-admin/expenses" element={<OfficeAdminExpenses />} />
        <Route path="/office-admin/expenses/new" element={<OfficeAdminExpenseForm />} />
        <Route path="/office-admin/expenses/:expenseId" element={<OfficeAdminExpenseDetail />} />
        <Route path="/office-admin/expenses/:expenseId/edit" element={<OfficeAdminExpenseForm />} />
        <Route path="/office-admin/tasks" element={<OfficeAdminTasks />} />
        <Route path="/office-admin/tasks/new" element={<OfficeAdminTaskForm />} />
        <Route path="/office-admin/tasks/:taskId" element={<OfficeAdminTaskDetail />} />
        <Route path="/office-admin/tasks/:taskId/edit" element={<OfficeAdminTaskForm />} />
      </Route>

      {/* Inventory Management Routes */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/inventory" element={<InventoryDashboard />} />
        <Route path="/inventory/products" element={<InventoryProducts />} />
        <Route path="/inventory/products/new" element={<InventoryProductForm />} />
        <Route path="/inventory/products/import" element={<InventoryImport />} />
        <Route path="/inventory/products/:productId" element={<InventoryProductForm />} />
        <Route path="/inventory/products/:productId/edit" element={<InventoryProductForm />} />
        <Route path="/inventory/categories" element={<InventoryCategories />} />
        <Route path="/inventory/suppliers" element={<InventorySuppliers />} />
        {/* Purchase Orders Routes */}
        <Route path="/inventory/purchase-orders" element={<PurchaseOrders />} />
        <Route path="/inventory/purchase-orders/new" element={<PurchaseOrderForm />} />
        <Route path="/inventory/purchase-orders/:poId" element={<PurchaseOrderDetail />} />
        <Route path="/inventory/purchase-orders/:poId/edit" element={<PurchaseOrderForm />} />
        {/* Pipeline Routes */}
        <Route path="/purchase-pipeline" element={<PurchasePipeline />} />
        <Route path="/purchase-workflow" element={<PurchaseWorkflow />} />
        <Route path="/sales-pipeline" element={<SalesPipeline />} />
        {/* Customer Routes */}
        <Route path="/inventory/customers" element={<Customers />} />
        <Route path="/inventory/customers/new" element={<CustomerForm />} />
        <Route path="/inventory/customers/:customerId" element={<CustomerDetail />} />
        <Route path="/inventory/customers/:customerId/edit" element={<CustomerForm />} />
        {/* Sales Orders Routes */}
        <Route path="/inventory/sales-orders" element={<SalesOrders />} />
        <Route path="/inventory/sales-orders/new" element={<SalesOrderForm />} />
        <Route path="/inventory/sales-orders/:orderId" element={<SalesOrderDetail />} />
        <Route path="/inventory/sales-orders/:orderId/invoice" element={<SalesInvoice />} />
        {/* Controls Routes (Phase 4) */}
        <Route path="/inventory/stock-adjustments" element={<StockAdjustments />} />
        <Route path="/inventory/stock-takes" element={<StockTakes />} />
        <Route path="/inventory/stock-takes/:stockTakeId" element={<StockTakeDetail />} />
        <Route path="/inventory/credit-notes" element={<CreditNotes />} />
        <Route path="/inventory/scanner" element={<MobileScanner />} />
        <Route path="/accounts" element={<AccountsDashboard />} />
        <Route path="/obligations" element={<ObligationsTracker />} />
      </Route>

      {/* 404 Redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppBadgeSync() {
  // Drives navigator.setAppBadge() / clearAppBadge() globally — must live
  // inside <AuthProvider> so it can read the current user.
  useAppBadge();
  // Auto-subscribe to Web Push whenever permission is already granted, so
  // notifications are "on by default" once the user grants the system prompt
  // — no manual "Enable Push Notifications" click needed.
  useAutoEnablePush();
  return null;
}

function App() {
  // Register service worker for push notifications
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      registerServiceWorker()
        .then(() => console.log('Service worker registered for push notifications'))
        .catch(err => console.log('Service worker registration skipped:', err.message));
    }
  }, []);

  // Screenshot deterrent: disable right-click, print screen, and common screenshot shortcuts
  useEffect(() => {
    // Disable right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable common screenshot keyboard shortcuts
    const handleKeyDown = (e) => {
      // Print Screen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        alert('Screenshots are not allowed in this application due to confidentiality policies.');
        return false;
      }
      
      // Ctrl+P (Print)
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault();
        alert('Printing is disabled for confidentiality reasons.');
        return false;
      }
      
      // Ctrl+Shift+S (Windows screenshot) or Cmd+Shift+S (Mac)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        return false;
      }
      
      // Ctrl+Shift+I (DevTools) - optional additional security
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'i' || e.key === 'I')) {
        e.preventDefault();
        return false;
      }
    };

    // Detect tab visibility change and blur content
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        document.body.classList.add('tab-inactive');
      } else {
        document.body.classList.remove('tab-inactive');
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppBadgeSync />
          <Suspense fallback={<PageLoader />}>
            <AppRoutes />
          </Suspense>
          <RedCodeAlert />
          <OfflineIndicator />
          <Toaster 
            position="top-center"
            richColors
            toastOptions={{
              style: {
                fontWeight: 500,
                fontSize: '14px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                borderRadius: '10px',
                padding: '12px 16px',
              },
              classNames: {
                success: '!bg-emerald-500 !text-white !border-emerald-600',
                error: '!bg-red-500 !text-white !border-red-600',
                warning: '!bg-amber-400 !text-slate-900 !border-amber-500',
                info: '!bg-blue-500 !text-white !border-blue-600',
                title: '!text-inherit',
                description: '!text-inherit !opacity-90',
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
